import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useAnchoredList } from '@/hooks/useAnchoredList';

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** more text the box also searches (e.g. a cost code's number and name separately) */
  searchText?: string;
  /** draws a thin gray line above this row (the start of a group of options) */
  dividerBefore?: boolean;
}

interface GenericSearchableSelectProps {
  id?: string;
  value: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder?: string;
  /** shown first, only while nothing is typed, and picking it gives `value` '' ("All ...") -- the filters of a list use it */
  clearLabel?: string;
  disabled?: boolean;
  ariaLabel?: string;
  /** shown when the typed text matches no option */
  emptyText?: string;
  /**
   * For a list that is searched on the server: the text typed ('' when nothing is) is reported here, the options are shown as given (no filtering of
   * their own), and `onLoadMore` adds a "Show more" row, at the end, that asks for the next page without closing the list.
   */
  onSearchChange?: (text: string) => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  /** extra classes on the text box (a dense table row passes its own field class, as it does to GenericSelect) */
  className?: string;
}

/** lower case, accents out, spaces collapsed: "  Dryw " and "dryw" are the same search */
const fold = (text: string) => text.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * A dropdown you can also TYPE in: the same single choice as GenericSelect (same look), but typing narrows the list to the options that
 * contain every word typed -- in the label or in `searchText` -- ignoring case, accents and extra spaces. The list is portaled to <body>
 * (hooks/useAnchoredList.ts), so a modal's body or footer never clips or covers it. Keyboard: ArrowUp/ArrowDown, Enter, Escape.
 */
export function GenericSearchableSelect({ id, value, onValueChange, options, placeholder = 'Select', clearLabel, disabled, ariaLabel, emptyText = 'No matches', className, onSearchChange, onLoadMore, hasMore }: GenericSearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [typing, setTyping] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listboxId = useId();
  const { anchorRef, listRef, listStyle, isInsideList } = useAnchoredList(open);

  const selected = options.find((option) => option.value === value);
  const shown = typing ? query : selected ? selected.label : '';

  const searchedOnServer = !!onSearchChange;
  const typedText = typing ? query : '';
  const reportSearch = useRef(onSearchChange);
  reportSearch.current = onSearchChange;
  useEffect(() => { reportSearch.current?.(typedText); }, [typedText]);

  const matches = useMemo(() => {
    if (searchedOnServer) return options;
    const words = typing ? fold(query).split(' ').filter(Boolean) : [];
    if (words.length === 0) return options;
    return options.filter((option) => {
      const haystack = fold(`${option.label} ${option.searchText ?? ''}`);
      return words.every((word) => haystack.includes(word));
    });
  }, [options, query, typing, searchedOnServer]);

  // the entries in display order: ["All ..." while nothing is typed], then the matches
  const entries = useMemo((): SearchableSelectOption[] => [
    ...(clearLabel && !(typing && query.trim() !== '') ? [{ value: '', label: clearLabel }] : []),
    ...matches,
  ], [clearLabel, matches, typing, query]);

  useEffect(() => { setHighlighted(0); }, [query, open]);

  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target) && !isInsideList(target)) { setOpen(false); setTyping(false); }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  });

  const choose = (next: string) => {
    onValueChange(next);
    setOpen(false);
    setTyping(false);
    setQuery('');
    // A chosen object is not text being edited: the box lets go of the focus (the list items keep it from leaving on mouse down), so no caret is left blinking
    inputRef.current?.blur();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') { setOpen(false); setTyping(false); }
    else if (event.key === 'ArrowDown') { event.preventDefault(); setOpen(true); setHighlighted((index) => Math.min(index + 1, entries.length - 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setHighlighted((index) => Math.max(index - 1, 0)); }
    else if (event.key === 'Enter') {
      event.preventDefault(); // Enter never submits the form this box sits in
      if (open && entries[highlighted]) choose(entries[highlighted].value);
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div ref={anchorRef} className="relative">
        <Input
          ref={inputRef}
          id={id}
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={shown}
          placeholder={selected ? undefined : (clearLabel ?? placeholder)}
          className={`font-form-input generic-form-field pr-8${className ? ` ${className}` : ''}`}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(event) => { setQuery(event.target.value); setTyping(true); setOpen(true); }}
          onKeyDown={onKeyDown}
        />
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
      </div>
      {open && listStyle && createPortal(
        <div ref={listRef} className="fixed z-[var(--z-overlay)] bg-white border-2 border-border rounded-lg shadow-lg max-h-60 overflow-auto" style={listStyle}>
          <ul id={listboxId} className="py-1" role="listbox">
            {entries.map((entry, index) => (
              <li
                key={entry.value || '__clear__'}
                role="option"
                aria-selected={entry.value === value}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => choose(entry.value)}
                onMouseEnter={() => setHighlighted(index)}
                className={`px-3 py-2 text-sm cursor-pointer ${index === highlighted ? 'bg-gray-100' : 'hover:bg-gray-100'} ${entry.value === '' ? 'text-gray-500' : 'text-gray-900'}${entry.dividerBefore ? ' border-t border-gray-200' : ''}`}
              >
                {entry.label}
              </li>
            ))}
            {onLoadMore && hasMore && (
              <li role="presentation" onMouseDown={(event) => event.preventDefault()} onClick={onLoadMore} className="px-3 py-2 text-sm cursor-pointer text-gray-500 hover:bg-gray-100">
                Show more
              </li>
            )}
            {matches.length === 0 && <li className="px-3 py-2 text-sm text-gray-500" role="presentation">{emptyText}</li>}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
}
